import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { FLOW } from 'app/model/adapters/dataflow/IDataflow';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { ConnectTransformation } from 'app/model/dataflow/connecttransformation';
import { FileBasedSettings } from 'app/model/dataflow/dataset-settings/file-based-settings';
import { DataFlowAdapterUtils } from 'app/model/adapters/dataflow/adapter.util';
import { CdmSettings } from 'app/model/dataflow/dataset-settings/cdm-settings';
import { TeamDeskSettings } from 'app/model/dataflow/dataset-settings/team-desk-settings';
import { DataFlowExpressionUtils } from '../expression.util';
import { QuickbaseSettings } from '../../../dataflow/dataset-settings/quickbase-settings';
import { CosmosDBStoreType, CosmosSqlSettings } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { PluginMetadataProcessService } from 'app/services/plugin-metadata-process.service';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ConnectTransformation),
    asynchronous: true
})
export class ConnectTransformationAdapter extends TransformationAdapter<ConnectTransformation> {
    public async getDslScript(connectTransformation: ConnectTransformation): Promise<string> {
        let containerProp = '';
        const { linkedService, datasetSettings, propertyMap } = connectTransformation;
        if (
            datasetSettings instanceof FileBasedSettings &&
            DataFlowAdapterUtils.hasExpression(datasetSettings.container)
        ) {
            containerProp = DataFlowAdapterUtils.getContainerProp(linkedService, datasetSettings.container);
        } else if (
            datasetSettings instanceof CdmSettings &&
            datasetSettings.subFormatSettings &&
            DataFlowAdapterUtils.hasExpression(datasetSettings.subFormatSettings.container)
        ) {
            containerProp = DataFlowAdapterUtils.getContainerProp(
                linkedService,
                datasetSettings.subFormatSettings.container
            );
        } else if (datasetSettings instanceof TeamDeskSettings) {
            const props = [];
            if (datasetSettings.schemaName.expression) {
                props.push(`table: ${DataFlowExpressionUtils.wrapExpressionConfigValue(datasetSettings.schemaName)}`);
            }
            if (datasetSettings.tableName.expression) {
                props.push(`view: ${DataFlowExpressionUtils.wrapExpressionConfigValue(datasetSettings.tableName)}`);
            }
            containerProp = props.join(', ');
        } else if (datasetSettings instanceof QuickbaseSettings) {
            const props = [];
            if (datasetSettings.schemaName.expression) {
                props.push(`table: ${DataFlowExpressionUtils.wrapExpressionConfigValue(datasetSettings.schemaName)}`);
            }
            if (datasetSettings.tableName.expression) {
                props.push(`report: ${DataFlowExpressionUtils.wrapExpressionConfigValue(datasetSettings.tableName)}`);
            }
            containerProp = props.join(', ');
        } else if (
            datasetSettings instanceof CosmosSqlSettings &&
            datasetSettings.storeType === CosmosDBStoreType.Analytical
        ) {
            const props: string[] = [`storeType: '${CosmosDBStoreType.Analytical}'`];
            TransformationAdapter.appendDatabaseProp(datasetSettings, props);
            containerProp = props.join(', ');
        } else if (propertyMap.size > 0) {
            const pluginMetadataProcessService = await getServiceInstance<PluginMetadataProcessService>(
                this.context.contextData,
                'PluginMetadataProcessService'
            );
            const connectorName = Metamodel.modelOf(linkedService)?.name;
            const props: string[] = [];
            propertyMap.forEach((value, key) => {
                if (!stringIsNullOrEmpty(value.expression)) {
                    props.push(
                        `${
                            pluginMetadataProcessService.getResourceName(connectorName, key) ?? key
                        }: ${DataFlowExpressionUtils.wrapExpressionConfigValue(value, value.requiredReturnType)}`
                    );
                }
            });
            TransformationAdapter.appendDatabaseProp(datasetSettings, props);
            containerProp = props.join(', ');
        } else {
            const props: string[] = [];
            TransformationAdapter.appendDatabaseProp(datasetSettings, props);
            containerProp = props.join(', ');
        }
        return `connect(${containerProp}) ${FLOW} ${connectTransformation.outputStream?.qualifiedName}`;
    }
}
